import {
  type DoughnutDatum,
  formatPercentage,
  noDecimalsFormatOptions,
} from "../../components"
import {
  type MarketShareDistributions,
  MarketShareKinds,
  type MarketShareLaboratory,
} from "../../types"
import { assertNever } from "../assertNever"
import {
  OTHERS_GROUP_KEY,
  buildMarketShareDonutSplits,
} from "./buildMarketShareDonutSplits"

export interface LaboratorySplit extends DoughnutDatum {
  laboratories: Array<{
    name: string
    formattedMainStat: string
    formattedPercentage: string
  }>
}

export const buildLaboratorySplits = ({
  laboratories,
  distribution,
  getLabColorByKind,
  formatValue,
  totalSales,
}: {
  laboratories: MarketShareLaboratory[]
  distribution: MarketShareDistributions
  getLabColorByKind: (kind: MarketShareKinds) => string
  formatValue: (value: number) => string
  totalSales: number
}) => {
  const mainStatOf = (lab: MarketShareLaboratory) => lab[distribution]?.mainStat ?? 0
  const formattedPercentage = (mainStat: number) => {
    if (totalSales === 0) {
      return formattedPercentage(0)
    }
    const percent = (mainStat * 100) / totalSales
    return formatPercentage(
      percent,
      percent < 1 ? undefined : noDecimalsFormatOptions,
    )
  }
  const mapToLab = (lab: MarketShareLaboratory) => {
    const mainStat = mainStatOf(lab)

    return {
      name: lab.name,
      formattedPercentage: formattedPercentage(mainStat),
      formattedMainStat: formatValue(mainStat),
    }
  }

  const groupRestIntoDivers = (
    restToGroup: MarketShareLaboratory[],
  ): MarketShareLaboratory =>
    restToGroup.reduce<MarketShareLaboratory>(
      (acc, lab) => {
        if (!acc[distribution]) {
          acc[distribution] = { mainStat: 0 }
        }
        acc[distribution].mainStat =
          (acc[distribution].mainStat ?? 0) + mainStatOf(lab)
        return acc
      },
      {
        name: "Divers",
        kind: MarketShareKinds.OTHER,
      },
    )

  return buildMarketShareDonutSplits<MarketShareLaboratory, LaboratorySplit>({
    items: laboratories,
    isEligible: (lab) => !!lab[distribution]?.mainStat,
    isFeatured: (lab) => lab.kind !== MarketShareKinds.OTHER,
    sortItem: (a, b) =>
      kindPriority(a.kind) - kindPriority(b.kind) || mainStatOf(b) - mainStatOf(a),
    mapToSplit: (lab) => ({
      label: lab.name,
      color: getLabColorByKind(lab.kind),
      value: mainStatOf(lab),
      key: `${lab.id} - ${lab.name}`,
      laboratories: [mapToLab(lab)],
    }),
    buildOthersSplit: (rest, restToGroup) => {
      const allLabs = [...rest, groupRestIntoDivers(restToGroup)]
      return {
        label: "Autres laboratoires",
        color: getLabColorByKind(MarketShareKinds.OTHER),
        value: allLabs.reduce((sum, lab) => sum + mainStatOf(lab), 0),
        key: OTHERS_GROUP_KEY,
        laboratories: allLabs.filter((lab) => mainStatOf(lab) > 0).map(mapToLab),
      }
    },
  })
}

const kindPriority = (kind: MarketShareKinds): number => {
  switch (kind) {
    case MarketShareKinds.SELECTED:
      return 1
    case MarketShareKinds.PRIORITY:
      return 2
    case MarketShareKinds.OTHER:
      return 3
    default:
      return assertNever(kind, 99, "kindPriority buildLaboratorySplits.ts")
  }
}
